import { useState, useRef, useEffect, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

const reviews = [
  { name: "Sarah M.", location: "Melbourne", text: "Oz Property Services helped us with a feature wall and TV mount. The finish came out clean and the communication was easy from start to finish." },
  { name: "James T.", location: "Broadmeadows", text: "They handled a few repairs, pressure washing and touch-ups before we leased out the property. Fast, practical and professional." },
  { name: "Ali R.", location: "Glenroy", text: "We had fencing and security upgrades done at our shop. Clear quote, reliable team and no messing around." },
];

const AVERAGE = 4.9;


const ReviewsSection = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);

  const updateCardWidth = useCallback(() => {
    requestAnimationFrame(() => {
      if (!scrollRef.current) return;
      const firstCard = scrollRef.current.querySelector<HTMLElement>("[data-review-card]");
      if (firstCard) {
        setCardWidth(firstCard.offsetWidth + 24);
      }
    });
  }, []);

  useEffect(() => {
    updateCardWidth();
    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateCardWidth, 150);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, [updateCardWidth]);

  const scrollTo = useCallback(
    (index: number) => {
      if (!scrollRef.current || !cardWidth) return;
      const clamped = Math.max(0, Math.min(index, reviews.length - 1));
      scrollRef.current.scrollTo({ left: clamped * cardWidth, behavior: "smooth" });
      setActiveIndex(clamped);
    },
    [cardWidth]
  );

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || !cardWidth) return;
    const idx = Math.round(scrollRef.current.scrollLeft / cardWidth);
    setActiveIndex(Math.max(0, Math.min(idx, reviews.length - 1)));
  }, [cardWidth]);

  const scrollRafRef = useRef<number | null>(null);
  const onScroll = useCallback(() => {
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      handleScroll();
    });
  }, [handleScroll]);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current !== null) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  return (
    <section id="reviews" className="bg-white py-20 md:py-28 overflow-hidden">
      <div className="container">
        {/* Header - summary on the left */}
        <div className="mb-14">
          <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] leading-tight">
            What Our Customers Say
          </h2>
          <p className="text-[#1a1a1a]/70 mt-1.5 text-base">
            Trusted by Melbourne homeowners, landlords and businesses
          </p>

          {/* Rating summary - left-aligned under heading */}
          <div className="flex items-center gap-5 mt-6">
            <span className="text-5xl font-black text-[#1a1a1a] leading-none tracking-tight">
              {AVERAGE}
            </span>
            <div className="flex flex-col gap-1.5">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#FFC300] text-[#FFC300]" />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-[#1a1a1a]/70">
                  Average Rating
                </span>
                <span className="text-xs text-[#1a1a1a]/70">
                  Based on customer feedback
                </span>
              </div>
            </div>
          </div>
        </div>


        {/* Carousel */}
        <div className="relative">
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-4 px-4"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {reviews.map((r) => (
              <div
                key={r.name}
                data-review-card
                className="snap-start shrink-0 w-[85%] sm:w-[75%] md:w-[calc(50%-12px)] bg-white rounded-2xl p-8 flex flex-col shadow-[0_2px_16px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-[18px] h-[18px] fill-[#FFC300] text-[#FFC300]" />
                  ))}
                </div>
                <p className="text-[#1a1a1a]/70 leading-relaxed flex-1 text-base">
                  "{r.text}"
                </p>
                <div className="mt-7 pt-5 border-t border-[#1a1a1a]/5 flex flex-col">
                  <span className="font-semibold text-sm text-[#1a1a1a]/75">{r.name} - {r.location}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Arrows */}
          <button
            onClick={() => scrollTo(activeIndex - 1)}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[#1a1a1a]/10 items-center justify-center shadow-sm hover:shadow-md transition-shadow disabled:opacity-30"
            disabled={activeIndex === 0}
            aria-label="Previous review"
          >
            <ChevronLeft className="w-5 h-5 text-[#1a1a1a]" />
          </button>
          <button
            onClick={() => scrollTo(activeIndex + 1)}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[#1a1a1a]/10 items-center justify-center shadow-sm hover:shadow-md transition-shadow disabled:opacity-30"
            disabled={activeIndex >= reviews.length - 1}
            aria-label="Next review"
          >
            <ChevronRight className="w-5 h-5 text-[#1a1a1a]" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-1 mt-6">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              aria-label={`Go to review ${i + 1}`}
              className="p-2 flex items-center justify-center"
            >
              <span
                className={`block h-2 rounded-full transition-all duration-300 ${
                  i === activeIndex
                    ? "bg-[#1a1a1a] w-5"
                    : "bg-[#1a1a1a]/15 w-2 hover:bg-[#1a1a1a]/30"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
